import PropTypes from 'prop-types';
import { createContext, useCallback, useEffect, useMemo, useReducer } from 'react';
// utils
import localStorageAvailable from '../utils/localStorageAvailable';
//
import { useSnackbar } from '../components/snackbar';
import { CMS_API } from '../config-global';
import { isValidToken, setSession, setUser } from './utils';

// ----------------------------------------------------------------------

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'UPDATE') {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }

  return state;
};

// ----------------------------------------------------------------------

// Normalize the CMS API user object so legacy components keep working
// (they read UserRole / roleId / role / UserID interchangeably).
const normalizeUser = (user) => {
  if (!user) return null;
  return {
    ...user,
    UserRole: user.RoleID,
    roleId: user.RoleID,
    role: user.Role,
    displayName: user.UserName,
    email: user.UserEmail,
    photoURL: user.PhotoURL,
  };
};

async function apiRequest(path, { method = 'GET', body, token } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await fetch(`${CMS_API}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(json.error || `Request failed (${response.status})`);
  }
  return json;
}

// ----------------------------------------------------------------------

export const AuthContext = createContext(null);

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const { enqueueSnackbar } = useSnackbar();

  const storageAvailable = localStorageAvailable();

  const initialize = useCallback(async () => {
    try {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
      const refreshToken = storageAvailable ? localStorage.getItem('refreshToken') : '';

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);
        const { user } = await apiRequest('/auth/me', { token: accessToken });
        const normalized = normalizeUser(user);
        setUser(JSON.stringify(normalized));
        dispatch({
          type: 'INITIAL',
          payload: { isAuthenticated: true, user: normalized },
        });
        return;
      }

      if (refreshToken) {
        // access token expired — try a silent refresh
        const data = await apiRequest('/auth/refresh', {
          method: 'POST',
          body: { refreshToken },
        });
        setSession(data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
        const normalized = normalizeUser(data.user);
        setUser(JSON.stringify(normalized));
        dispatch({
          type: 'INITIAL',
          payload: { isAuthenticated: true, user: normalized },
        });
        return;
      }

      dispatch({
        type: 'INITIAL',
        payload: { isAuthenticated: false, user: null },
      });
    } catch (error) {
      setSession(null);
      setUser(null);
      localStorage.removeItem('refreshToken');
      dispatch({
        type: 'INITIAL',
        payload: { isAuthenticated: false, user: null },
      });
    }
  }, [storageAvailable]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const login = useCallback(
    async (email, password) => {
      try {
        const data = await apiRequest('/auth/login', {
          method: 'POST',
          body: { email, password },
        });

        setSession(data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
        const normalized = normalizeUser(data.user);
        setUser(JSON.stringify(normalized));

        dispatch({
          type: 'LOGIN',
          payload: { user: normalized },
        });

        enqueueSnackbar('Login successful', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar(error.message, { variant: 'error' });
      }
    },
    [enqueueSnackbar]
  );

  const register = useCallback(
    async (USERNAME, PHONENUMBER, EVENTVENUE, USEREMAIL, USERPASSWORD) => {
      try {
        const data = await apiRequest('/auth/register', {
          method: 'POST',
          body: {
            userName: USERNAME,
            phoneNumber: PHONENUMBER,
            eventVenue: EVENTVENUE,
            email: USEREMAIL,
            password: USERPASSWORD,
          },
        });

        setSession(data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
        const normalized = normalizeUser(data.user);
        setUser(JSON.stringify(normalized));

        dispatch({
          type: 'REGISTER',
          payload: { user: normalized },
        });
      } catch (error) {
        enqueueSnackbar(error.message || 'Registration failed', { variant: 'error' });
      }
    },
    [enqueueSnackbar]
  );

  const updateProfile = useCallback(
    async (
      userID,
      userFirstName,
      userEmail,
      userPhotoURL,
      userPhoneNo,
      userCountry,
      userAddress,
      userState,
      userCity,
      userZipCode,
      userAbout
    ) => {
      try {
        const accessToken = localStorage.getItem('accessToken');
        const data = await apiRequest('/auth/profile', {
          method: 'PUT',
          token: accessToken,
          body: {
            userName: userFirstName,
            photoURL: userPhotoURL,
            phoneNumber: userPhoneNo,
            country: userCountry,
            address: userAddress,
            state: userState,
            city: userCity,
            zipCode: userZipCode,
            about: userAbout,
          },
        });

        const normalized = normalizeUser(data.user);
        setUser(JSON.stringify(normalized));
        dispatch({
          type: 'UPDATE',
          payload: { user: normalized },
        });

        enqueueSnackbar('Profile updated', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar(error.message || 'Update profile failed', { variant: 'error' });
      }
    },
    [enqueueSnackbar]
  );

  const logout = useCallback(() => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      // best-effort server-side revocation
      apiRequest('/auth/logout', { method: 'POST', body: { refreshToken } }).catch(() => {});
    }
    setSession(null);
    setUser(null);
    localStorage.removeItem('refreshToken');
    dispatch({
      type: 'LOGOUT',
    });
  }, []);

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      method: 'jwt',
      login,
      register,
      updateProfile,
      logout,
    }),
    [state.isAuthenticated, state.isInitialized, state.user, login, logout, register, updateProfile]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
